import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Camera, Sparkle, Trash, ArrowRight, WarningCircle, CheckCircle, X, FileText, ImageSquare, Microphone, ListChecks } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import PhotoVoicePunchModal from "@/components/PhotoVoicePunchModal";

// Renders as AdminShell wrapper unless embedded (from AskRadar hub tabs).
const Shell = ({ embedded, children }) => embedded ? <>{children}</> : <AdminShell>{children}</AdminShell>;

export default function AdminPhotoCommand({ embedded = false }) {
  const [mode, setMode] = useState("scanner"); // "scanner" | "punch"
  const [photos, setPhotos] = useState([]); // [{file, url}]
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (mode === "punch" && projects.length === 0) {
      api.get("/projects").then(r => setProjects(r.data?.projects || [])).catch(() => setProjects([]));
    }
  }, [mode, projects.length]);

  const addFiles = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    if (photos.length + arr.length > 8) {
      toast.error("Max 8 photos per scan");
      return;
    }
    const next = arr.map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setPhotos((prev) => [...prev, ...next]);
  };
  const removeAt = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const scan = async () => {
    if (!photos.length) return;
    setScanning(true);
    setResult(null);
    const fd = new FormData();
    photos.forEach((p) => fd.append("files", p.file));
    try {
      const r = await api.post("/ai/photo-command", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (!(r.data?.wo_numbers || []).length) {
        toast.error("No work-order numbers found in these photos");
      } else {
        toast.success(`Detected ${r.data.wo_numbers.length} work order(s)`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const applyBulkClose = async () => {
    if (!result?.resolved_jobs?.length) return;
    if (!window.confirm(`Close ${result.resolved_jobs.length} work order(s)? Jobs without notes AND photos will go to "Needs Info" instead of Completed.`)) return;
    setApplying(true);
    try {
      const r = await api.post("/jobs/bulk-close", {
        job_ids: result.resolved_jobs.map((j) => j.id),
        reason: result.reason || "Bulk-closed via ProMan photo command",
      });
      const done = r.data.completed?.length || 0;
      const needs = r.data.needs_info?.length || 0;
      const skip = r.data.skipped?.length || 0;
      toast.success(`${done} completed · ${needs} needs info · ${skip} skipped`);
      setResult(null);
      setPhotos([]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk close failed");
    } finally {
      setApplying(false);
    }
  };

  const applyBulkCreateParkingLot = async () => {
    const njs = result?.new_jobs || [];
    if (!njs.length) return;
    if (!window.confirm(`Drop ${njs.length} new job(s) into Parking Lot?`)) return;
    setApplying(true);
    try {
      const r = await api.post("/parking-lot/bulk", {
        entries: njs.map((nj) => ({
          property_address: nj.property_address,
          unit: nj.unit || "",
          quick_note: nj.note || "",
        })),
      });
      toast.success(`Dropped ${r.data.total} entries to Parking Lot`);
      setResult(null);
      setPhotos([]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk create failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Shell embedded={embedded}>
      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="photo-command-page">
        <div className="flex items-center gap-3">
          <Sparkle weight="fill" size={30} className="text-violet-600" />
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
            Ask Radar · Photo Command
          </h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Snap a photo of your whiteboard, notepad, or a text message with WO numbers on it. Radar reads it and offers to bulk close the listed work orders. Anything missing notes or photos moves to <b>ClosureRadar™</b> — never to Completed.
        </p>

        {/* Mode toggle */}
        <div className="mt-5 inline-flex border-2 border-[#0f172a] bg-white" data-testid="photo-command-mode-toggle">
          <button onClick={() => setMode("scanner")} data-testid="photo-mode-scanner"
            className={`px-3 h-10 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${
              mode === "scanner" ? "bg-violet-600 text-white" : "text-slate-700 hover:bg-slate-50"
            }`}>
            <FileText weight="bold" size={13}/> WO Scanner
          </button>
          <button onClick={() => setMode("punch")} data-testid="photo-mode-punch"
            className={`px-3 h-10 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${
              mode === "punch" ? "bg-fuchsia-600 text-white" : "text-slate-700 hover:bg-slate-50"
            }`}>
            <ListChecks weight="bold" size={13}/> Snap + Say → Punch Item
          </button>
        </div>

        {mode === "punch" && (
          <div className="mt-6 rugged-card p-5" data-testid="photo-punch-launch-card">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle weight="fill" size={16} className="text-fuchsia-600"/>
              <h2 className="font-display font-black text-lg uppercase tracking-tight">Snap + Say</h2>
            </div>
            <p className="text-sm text-slate-600 max-w-xl">
              One-shot workflow: attach a photo of the issue, tap the mic and describe it, and Radar creates a punch item on the project you pick — photo attached, priority auto-set for safety words.
            </p>
            <button onClick={() => setShowPunchModal(true)} data-testid="photo-punch-open"
              className="mt-4 h-12 px-5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2">
              <Microphone weight="fill" size={14}/> Open Snap + Say
            </button>
            {showPunchModal && (
              <PhotoVoicePunchModal
                projects={projects}
                onClose={() => setShowPunchModal(false)}
                onSaved={() => setShowPunchModal(false)}
              />
            )}
          </div>
        )}

        {mode === "scanner" && (
        <>
        {/* Upload zone */}
        <div className="mt-6 rugged-card p-5" data-testid="photo-command-upload">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500 mb-3">
            <Camera size={14} /> Attach up to 8 photos
          </div>
          <div className="flex gap-3 flex-wrap">
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <img src={p.url} alt="" className="h-24 w-24 object-cover border-2 border-[#0f172a]" />
                <button
                  onClick={() => removeAt(i)}
                  data-testid={`photo-command-remove-${i}`}
                  className="absolute -top-2 -right-2 h-6 w-6 bg-red-600 text-white flex items-center justify-center border-2 border-white"
                  aria-label="Remove photo"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            ))}
            {photos.length < 8 && (
              <button
                onClick={() => inputRef.current?.click()}
                data-testid="photo-command-add-btn"
                className="h-24 w-24 border-2 border-dashed border-[#0f172a] flex flex-col items-center justify-center text-slate-500 hover:bg-slate-50 text-[10px] font-black uppercase tracking-widest"
              >
                <Camera size={22} />
                <span className="mt-1">Add</span>
              </button>
            )}
            <input
              type="file"
              ref={inputRef}
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
              data-testid="photo-command-input"
            />
          </div>
          <button
            onClick={scan}
            disabled={!photos.length || scanning}
            data-testid="photo-command-scan-btn"
            className="mt-5 h-12 px-6 bg-violet-600 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkle size={16} weight="fill" />
            {scanning ? "Scanning…" : `Scan ${photos.length} Photo${photos.length === 1 ? "" : "s"}`}
          </button>
        </div>

        {/* Results */}
        {result && (
          <div className="mt-6 rugged-card p-5" data-testid="photo-command-result">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-violet-600" />
              <span className="text-xs font-black uppercase tracking-widest">Detected</span>
              <span className="px-2 py-0.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest">
                Action: {result.action || "unknown"}
              </span>
            </div>
            {result.reason && <div className="mt-2 text-sm text-slate-700 italic">{result.reason}</div>}
            {result.raw_transcription && (
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer font-bold text-slate-500 uppercase tracking-widest">Raw transcription</summary>
                <pre className="mt-2 p-3 bg-slate-50 border text-xs whitespace-pre-wrap font-mono">{result.raw_transcription}</pre>
              </details>
            )}

            <div className="mt-4 text-xs font-black uppercase tracking-widest text-slate-500">
              WO Numbers: {result.wo_numbers?.join(", ") || "—"}
            </div>
            <div className="mt-2 space-y-2" data-testid="photo-command-resolved">
              {(result.resolved_jobs || []).map((j) => {
                const willNeedInfo = !(j.has_notes && j.photo_count > 0);
                return (
                  <div key={j.id} data-testid={`photo-command-job-${j.work_order_number}`}
                    className="border-2 border-slate-200 p-3 flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs text-slate-500">WO# {j.work_order_number}</span>
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-black text-sm">{j.title || "(no title)"}</div>
                      <div className="text-[11px] text-slate-500">{j.property_address}{j.unit ? ` · ${j.unit}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className={j.has_notes ? "text-emerald-700" : "text-red-600"}>
                        {j.has_notes ? <CheckCircle size={11}/> : <WarningCircle size={11}/>} Notes
                      </span>
                      <span className={j.photo_count > 0 ? "text-emerald-700" : "text-red-600"}>
                        <ImageSquare size={11} className="inline"/> {j.photo_count} photo{j.photo_count===1?"":"s"}
                      </span>
                    </div>
                    {willNeedInfo && (
                      <span className="px-2 py-1 bg-amber-100 text-amber-900 text-[10px] font-black uppercase tracking-widest border border-amber-300">
                        → Needs Info
                      </span>
                    )}
                  </div>
                );
              })}
              {result.wo_numbers?.length > 0 && result.resolved_jobs?.length === 0 && (
                <div className="text-sm text-slate-500 italic">No matching jobs found for the detected WO numbers.</div>
              )}
            </div>

            {result.resolved_jobs?.length > 0 && (
              <button
                onClick={applyBulkClose}
                disabled={applying}
                data-testid="photo-command-apply-btn"
                className="mt-5 h-12 px-6 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-40"
              >
                {applying ? "Applying…" : `Close ${result.resolved_jobs.length} Job(s)`} <ArrowRight size={14} weight="bold"/>
              </button>
            )}

            {(result.new_jobs || []).length > 0 && (
              <div className="mt-6 border-t-2 border-slate-200 pt-4" data-testid="photo-command-new-jobs">
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                  <Sparkle size={12} weight="fill" /> New Jobs Detected
                </div>
                <div className="space-y-2">
                  {result.new_jobs.map((nj, i) => (
                    <div key={i} data-testid={`photo-command-new-job-${i}`}
                      className="border-2 border-amber-200 bg-amber-50 p-3">
                      <div className="font-black text-sm">
                        {nj.property_address}{nj.unit ? ` · ${nj.unit}` : ""}
                      </div>
                      {nj.note && <div className="text-xs italic text-slate-700 mt-1">"{nj.note}"</div>}
                    </div>
                  ))}
                </div>
                <button
                  onClick={applyBulkCreateParkingLot}
                  disabled={applying}
                  data-testid="photo-command-parkinglot-btn"
                  className="mt-4 h-11 px-5 bg-amber-500 text-white text-xs font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2"
                >
                  🅿️ Drop {result.new_jobs.length} to ClosureRadar
                </button>
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>
    </Shell>
  );
}
